import { DateTime } from 'luxon';
import { getShortMonthLabelFromISO, type GeneratedReportRanges, type Timestep } from 'app-components/utils/date';

// Restores the custom x-axis label behavior lost in the AMCharts -> ECharts migration
// (SIMPL-34454). Given the ordered data points (each carrying a raw ISO `startOn`) and
// the selected range/timestep, this returns a sparse array of labels aligned to the
// points: '' hides a label, a non-empty string shows it. The category axis renders
// every point (so tooltips and data density are untouched) but only paints these labels.
//
// Returns `null` to signal "no override" — the caller then falls back to the default
// axis behavior (show every `category` string, thinned by ECharts `hideOverlap`). This
// happens for un-dated data (e.g. synthetic single-period points) and for timesteps the
// spec does not define when no known range is present.

export type AxisLabelPoint = {
  startOn?: string;
  endOn?: string;
  category?: string;
};

type LabelStrategy =
  | { kind: 'default' } // defer to the caller's default (equivalent to "show every day")
  | { kind: 'everyNthDay'; n: number }
  | { kind: 'monthly'; everyNMonths: number } // month label positioned nearest the 15th
  | { kind: 'janJul' }; // every 6 months, only January & July labeled

// Day-span thresholds used when a range isn't explicitly one of the named buttons
// (ALL_DATES / dynamic YTD, per ticket items 7-9). Kept slightly generous so a range
// that is "about" N months lands in the right bucket regardless of month lengths.
const DAYS_IN_MONTH = 31;
const DAYS_IN_SIX_MONTHS = 186;
const DAYS_IN_YEAR = 366;

const parseDate = (iso?: string): DateTime | null => {
  if (!iso) return null;
  const d = DateTime.fromISO(iso);
  return d.isValid ? d : null;
};

const monthKey = (d: DateTime): number => d.year * 12 + (d.month - 1);

const daySpan = (dates: (DateTime | null)[]): number => {
  const valid = dates.filter((d): d is DateTime => d !== null);
  if (valid.length < 2) return 0;
  const first = valid[0];
  const last = valid[valid.length - 1];
  return Math.abs(last.diff(first, 'days').days);
};

// Span rules for data dense enough to label at day granularity: daily labels under a month,
// then month labels thinning out as the span grows.
const deriveFromDaySpan = (spanDays: number, preferMonthlyDateLabels: boolean): LabelStrategy => {
  if (spanDays < DAYS_IN_MONTH) return { kind: 'default' }; // daily labels (< 1 month)
  if (spanDays <= DAYS_IN_SIX_MONTHS) return { kind: 'monthly', everyNMonths: 1 };
  if (spanDays <= DAYS_IN_YEAR) return { kind: 'monthly', everyNMonths: preferMonthlyDateLabels ? 1 : 2 };
  return { kind: 'janJul' };
};

// For ALL_DATES / QTD / YTD (or an unknown range), pick the strategy from how the data
// is bucketed (timestep) and how much history it covers (span). Uncovered timesteps
// (WEEK, QUARTER, etc.) fall back to the default so those charts are never regressed.
const deriveFromSpan = (
  timestep: Timestep | undefined,
  spanDays: number,
  preferMonthlyDateLabels: boolean,
  denseDateLabels: boolean,
): LabelStrategy => {
  // `denseDateLabels` callers have one point per sub-monthly bucket, so the day-span rules read
  // correctly no matter which bucket the API picked. Needed by the asset Market Value chart, whose
  // 3-month-and-longer ranges come back WEEK-bucketed but still want month labels (SIMPL-34721).
  // Unflagged WEEK charts (OpenSaveReports) keep falling through to 'default' below.
  if (denseDateLabels || timestep === 'DAY') {
    return deriveFromDaySpan(spanDays, preferMonthlyDateLabels);
  }
  if (timestep === 'MONTH') {
    // Up to ~2 years of monthly bars (≤24 bars, the watchlist cap) → show every month.
    // Beyond that (e.g. 7-year ALL_DATES history) → thin to every other month.
    if (spanDays <= 2 * DAYS_IN_YEAR) return { kind: 'monthly', everyNMonths: 1 };
    return { kind: 'monthly', everyNMonths: 2 };
  }
  // WEEK / TWO_WEEKS / HALF_MONTH / QUARTER / SIX_MONTHS / YEAR / NONE / OVER-TIME:
  // no spec rule -> keep today's label behavior.
  return { kind: 'default' };
};

const resolveStrategy = (
  range: GeneratedReportRanges | undefined,
  timestep: Timestep | undefined,
  spanDays: number,
  preferMonthlyDateLabels: boolean,
  denseDateLabels: boolean,
): LabelStrategy => {
  switch (range) {
    case 'SAME_DAY_LAST_MONTH': // 1M
      return { kind: 'everyNthDay', n: 3 };
    case 'SAME_DAY_LAST_3_MONTHS': // 3M
    case 'SAME_DAY_LAST_6_MONTHS': // 6M
      return { kind: 'monthly', everyNMonths: 1 };
    case 'SAME_DAY_LAST_YEAR': // 1Y
      return { kind: 'monthly', everyNMonths: 2 };
    case 'SAME_DAY_LAST_5_YEARS': // 5Y
      return { kind: 'janJul' };
    case 'QUARTER_TO_DATE': // QTD (items 7)
    case 'YEAR_TO_DATE': // YTD (items 8, 9)
    case 'ALL_DATES':
    default: // unknown / dynamic history
      return deriveFromSpan(timestep, spanDays, preferMonthlyDateLabels, denseDateLabels);
  }
};

// Label every nth day using the point's existing pre-formatted `category` text
// (day strategies keep the day-level label, e.g. "Jun 9"). Anchored to the first point.
const buildEveryNthDay = (points: AxisLabelPoint[], n: number): string[] =>
  points.map((p, i) => (i % n === 0 ? (p.category ?? '') : ''));

// Given the chronological indices to label with a month, emit month labels applying the
// global year rule: show the year only on January, or — if a year boundary was crossed
// but January isn't itself a visible label — on the first visible month of the new year.
// When annotateYears is false (dense every-month layouts), year suffixes are omitted so all
// labels stay the same short width and hideOverlap doesn't single out "Jan 2025" for removal.
const emitMonthLabels = (
  points: AxisLabelPoint[],
  dates: (DateTime | null)[],
  selected: number[],
  annotateYears = true,
): string[] => {
  const labels = new Array<string>(points.length).fill('');
  let prevVisibleYear: number | null = null;
  selected.forEach((idx) => {
    const d = dates[idx];
    const iso = points[idx].startOn;
    if (!d || !iso) return;
    const month = getShortMonthLabelFromISO(iso);
    let showYear = false;
    if (annotateYears) {
      if (prevVisibleYear === null) {
        showYear = d.month === 1; // first visible label only carries a year if it's January
      } else if (d.year !== prevVisibleYear) {
        showYear = true; // first visible label of a new year (January or otherwise)
      }
    }
    prevVisibleYear = d.year;
    labels[idx] = showYear ? `${month} ${d.year}` : month;
  });
  return labels;
};

// Pick, for each qualifying month, the data-point index closest to the target day of the
// month (the 15th for rolling month labels, the 1st for Jan/Jul semiannual labels).
const selectIndicesNearestDay = (
  dates: (DateTime | null)[],
  qualifies: (d: DateTime) => boolean,
  targetDay: number,
): number[] => {
  const bestByMonth = new Map<number, { index: number; day: number }>();
  dates.forEach((d, i) => {
    if (!d || !qualifies(d)) return;
    const key = monthKey(d);
    const current = bestByMonth.get(key);
    if (current === undefined || Math.abs(d.day - targetDay) < Math.abs(current.day - targetDay)) {
      bestByMonth.set(key, { index: i, day: d.day });
    }
  });
  return [...bestByMonth.values()].map((best) => best.index).sort((a, b) => a - b);
};

const buildMonthly = (
  points: AxisLabelPoint[],
  dates: (DateTime | null)[],
  everyNMonths: number,
  annotateYears?: boolean,
): string[] => {
  // Cadence is anchored to the first month present, so "every 2 months" phases off the
  // start of the visible range (e.g. Jul, Sep, Nov, Jan, ...).
  const uniqueMonths: number[] = [];
  dates.forEach((d) => {
    if (!d) return;
    const key = monthKey(d);
    if (!uniqueMonths.includes(key)) uniqueMonths.push(key);
  });
  const qualifyingMonths = new Set<number>();
  uniqueMonths.forEach((key, idx) => {
    if (idx % everyNMonths === 0) qualifyingMonths.add(key);
  });
  const selected = selectIndicesNearestDay(dates, (d) => qualifyingMonths.has(monthKey(d)), 15);
  // When caller explicitly passes annotateYears, honour it (ResizeObserver-driven width check).
  // Otherwise fall back: annotate when labels are sparse enough (everyNMonths > 1) that year
  // context would otherwise be lost. Dense every-month layouts default off so "Jan 2026" (wider
  // than "Jan") doesn't get singled out by hideOverlap when the container is at default width.
  return emitMonthLabels(points, dates, selected, annotateYears ?? true);
};

const buildJanJul = (points: AxisLabelPoint[], dates: (DateTime | null)[]): string[] => {
  const selected = selectIndicesNearestDay(dates, (d) => d.month === 1 || d.month === 7, 1);
  return emitMonthLabels(points, dates, selected);
};

export const buildDateAxisLabels = (
  points: AxisLabelPoint[],
  opts: {
    range?: GeneratedReportRanges;
    timestep?: Timestep;
    preferMonthlyDateLabels?: boolean;
    denseDateLabels?: boolean;
    annotateYears?: boolean;
  },
): string[] | null => {
  if (!points || points.length === 0) return null;

  const dates = points.map((p) => parseDate(p.startOn));
  // Need at least two real dates to reason about spacing/spans; otherwise defer to default.
  if (dates.filter(Boolean).length < 2) return null;

  const strategy = resolveStrategy(
    opts.range,
    opts.timestep,
    daySpan(dates),
    opts.preferMonthlyDateLabels ?? false,
    opts.denseDateLabels ?? false,
  );
  switch (strategy.kind) {
    case 'everyNthDay':
      return buildEveryNthDay(points, strategy.n);
    case 'monthly':
      return buildMonthly(points, dates, strategy.everyNMonths, opts.annotateYears);
    case 'janJul':
      return buildJanJul(points, dates);
    case 'default':
    default:
      return null;
  }
};
